import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { useState, type ReactElement } from 'react';

import { notificationsConfig } from '@/helpers/notifications';
import type { IErrorResponse, ProcessedData } from '@/interfaces/model/fetch.interface';

type IFetch = ((data: any) => Promise<ProcessedData<unknown>>) | (() => Promise<ProcessedData<unknown>>);
// Excluir respuesta IError del success data;
type DataSuccess<F extends IFetch> = Exclude<Awaited<ReturnType<F>>['data'], IErrorResponse>;
type ICode<F extends IFetch> = Awaited<ReturnType<F>>['code'];
type PayloadType<F extends (...args: any) => any> = Parameters<F>[0];

/**
 * Hook personalizado que centraliza la lógica de las llamadas asíncronas a APIs (fetch),
 * la gestión de estados de carga (loading) independientes y el manejo de notificaciones de error.
 *
 * Una de sus características es la gestión de estados 'loading' por nombre de operación,
 * permitiendo monitorear el estado de múltiples peticiones fetch simultáneamente.
 *
 * @example
 * Params {loading:['getUsers', 'postForm'] as const}
 * Return {loading: {getUsers: false, postForm: false}, triggerFetch}
 */
const useApiData = <T extends readonly string[]>({ loading: loadingDefault }: { readonly loading: T }) => {
	const loadingDefaultMapped = loadingDefault.reduce(
		(acc, curr) => ({ ...acc, [curr]: false }),
		// eslint-disable-next-line @typescript-eslint/prefer-reduce-type-parameter
		{} as Record<T[number], boolean>,
	);
	const [loading, setLoading] = useState<Record<T[number], boolean>>(loadingDefaultMapped);
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });

	const triggerFetch = async <F extends IFetch>({
		payload,
		fetch,
		name,
		error,
		onCloseError,
	}: {
		payload?: PayloadType<F>;
		fetch: F;
		readonly name: T[number];
		error?: string | ReactElement;
		onCloseError?: () => void;
	}): Promise<{ data?: DataSuccess<F>; code: ICode<F>; message?: string }> => {
		setLoading((state) => ({ ...state, [name]: true }));
		const { code, data } = await fetch(payload);
		setLoading((state) => ({ ...state, [name]: false }));
		if (code === 406 && (data as IErrorResponse)?.message  ) {
			const { errores = [], message } = data as IErrorResponse;
			openNotification(
				notificationTemplate.error406({
					message,
					errores,
				}),
			);
			return { code, message: (data as IErrorResponse)?.message };
		}
		
		if (code !== 200) {
			openNotification(
				notificationTemplate.errorMessage(
					error ?? 'Error inesperado, inténtelo de nuevo más tarde.',
					onCloseError,
				),
			);
			return { code, message: (data as IErrorResponse)?.message };
		}

		return { code, data: data as DataSuccess<F> };
	};

	return { triggerFetch, loading };
};

export default useApiData;
